// مصروف يوم كامل — المسار المسائي الهادئ.
//
// قاعدتان تحكمان هذه الشاشة:
// ١) لا تفتح شاشة فارغة تسأل «ماذا صرفت اليوم؟» — هذا استدعاء من الصفر وهو أصعب
//    ما يفعله الدماغ. اعرض البنود المعتادة جاهزة ليؤكّد أو يتجاوز؛ التعرّف أسهل.
// ٢) مطابقة الجيب: اسأله كم كان معه لا ما صرفه. الفجوة تلتقط المصروفات الصغيرة
//    المنسية، وهي أكبر تسريب في ميزانية الأسرة وأول ما يُنسى.

import { fmt, money, esc, DAY_MS, startOfDay, dayLabel } from '../util.js';
import { addEntry, addType, typeById, destName, baseCur, baseSym, activeTypes, activeDests } from '../store.js';
import { topTypes, autoDestFor, orderedTypes } from '../model.js';
import { keypad, backBar } from '../ui.js';
import * as calc from '../calc.js';

export function freshDraft(){
  return { dayOffset: 0, values: {}, active: null, cash: 0, rows: null, calc: calc.create() };
}

/**
 * يثبّت ناتج الحاسبة في الخانة النشطة.
 * يُستدعى بعد كل ضغطة لا عند الانتقال فقط، فيبقى مجموع اليوم حيّاً أمام المستخدم
 * بدل أن يقفز فجأة حين ينتقل لخانة أخرى.
 */
function commit(d){
  if (!d.active) return;
  const v = calc.value(d.calc);
  if (d.active === '__cash'){
    d.cash = v && v > 0 ? v : 0;
  } else if (v && v > 0){
    d.values[d.active] = v;
  } else {
    delete d.values[d.active];
  }
}

function rows(app){
  if (!app.dayDraft.rows) app.dayDraft.rows = topTypes(7).map(t => t.id);
  return app.dayDraft.rows.filter(id => typeById(id));
}

export function render(app){
  const d = app.dayDraft;
  const list = rows(app);
  const sym = baseSym();

  let total = 0;
  list.forEach(id => { total += +(d.values[id] || 0); });
  const cash = +(d.cash || 0);
  const gap = cash ? cash - total : 0;

  const dateChips = [0, 1, 2].map(off => {
    const ts = startOfDay(Date.now()) - off * DAY_MS;
    return `<button type="button" class="chip" data-dayoff="${off}" aria-pressed="${d.dayOffset === off}">${esc(dayLabel(ts))}</button>`;
  }).join('');

  const cellText = id => {
    const v = id === '__cash' ? d.cash : d.values[id];
    return v ? calc.formatValue(v) : '—';
  };

  const bodyRows = list.map(id => {
    const t = typeById(id);
    const destId = autoDestFor(id);
    const v = d.values[id];
    return `<button type="button" class="row" data-day="${id}" aria-current="${d.active === id}">
      <span class="nm">${esc(t.name)}<small>${destId ? 'وجهة تلقائية: ' + esc(destName(destId)) : 'ستُسجَّل على أول وجهة'}</small></span>
      <span class="val${v ? '' : ' dim'}">${esc(cellText(id))}</span>
    </button>`;
  }).join('');

  const others = orderedTypes().filter(t => !list.includes(t.id));
  const more = others.length
    ? `<div class="dim t" style="padding:10px 0 2px"><div class="lab">بند آخر</div>
        <div class="strip">${others.map(t =>
          `<button type="button" class="pill" data-dayadd="${t.id}">${esc(t.name)}</button>`).join('')}</div></div>`
    : '';

  const cashRow = `<button type="button" class="row dashed" data-day="__cash" aria-current="${d.active === '__cash'}" style="margin-top:8px">
      <span class="nm">كم كان معك نقداً هذا الصباح؟<small>اختياري — لالتقاط ما نسيته</small></span>
      <span class="val${cash ? '' : ' dim'}">${esc(cellText('__cash'))}</span>
    </button>`;

  const gapBar = cash && gap > 0
    ? `<div class="gapbar"><span>بقي ${money(gap)} ${sym} بلا تفسير</span>
        <button type="button" data-act="gapfill">سجّلها مصروف يد</button></div>`
    : (cash && gap < 0
        ? `<div class="gapbar"><span>سجّلت ${money(-gap)} ${sym} أكثر مما كان معك — راجع المبالغ</span></div>`
        : '');

  const n = list.filter(id => d.values[id]).length;

  return backBar('مصروف يوم كامل', 'اضغط البند واكتب مبلغه · اضغطه ثانيةً للحاسبة')
    + `<div class="dim" style="padding-top:4px"><div class="strip noscroll">${dateChips}</div></div>`
    + `<div class="scroll">${bodyRows}${more}
        <div class="total"><span>مجموع اليوم</span><span class="mono">${money(total)} ${sym}</span></div>
        ${cashRow}${gapBar}</div>`
    + keypad()
    + `<button type="button" class="cta" data-act="saveday" ${total > 0 ? '' : 'disabled'}>
        حفظ ${n} ${n === 1 ? 'حركة' : 'حركات'} · ${money(total)} ${sym}</button>`;
}

export function onClick(app, btn){
  const d = app.dayDraft;

  if (btn.dataset.key){
    if (!d.active) return;
    d.calc = calc.press(d.calc, btn.dataset.key);
    commit(d);
    return app.render();
  }
  if (btn.dataset.day){
    const id = btn.dataset.day;
    // ضغطة ثانية على الخانة النشطة تفتح الحاسبة — بلا زرّ إضافي في كل صف
    if (d.active === id){
      return app.openCalc(calc.value(d.calc), v => {
        d.calc = calc.fromNumber(v);
        commit(d);
        app.render();
      });
    }
    commit(d);                       // نثبّت الخانة السابقة قبل مغادرتها
    d.active = id;
    d.calc = calc.fromNumber(id === '__cash' ? d.cash : d.values[id]);
    return app.render();
  }
  if (btn.dataset.dayadd){
    commit(d);
    d.rows.push(btn.dataset.dayadd);
    d.active = btn.dataset.dayadd;
    d.calc = calc.fromNumber(d.values[d.active]);
    return app.render();
  }
  if (btn.dataset.dayoff !== undefined && btn.dataset.dayoff !== ''){
    d.dayOffset = +btn.dataset.dayoff;
    return app.render();
  }

  const act = btn.dataset.act;

  if (act === 'gapfill'){
    commit(d);
    let total = 0;
    rows(app).forEach(id => { total += +(d.values[id] || 0); });
    const gap = +(d.cash || 0) - total;
    if (gap > 0){
      let hand = activeTypes().find(t => t.name === 'مصروف يد') || addType('مصروف يد');
      if (hand){
        if (!d.rows.includes(hand.id)) d.rows.push(hand.id);
        d.values[hand.id] = Math.round(((+d.values[hand.id] || 0) + gap) * 100) / 100;
        d.active = hand.id;
        d.calc = calc.fromNumber(d.values[hand.id]);
      }
    }
    return app.render();
  }

  if (act === 'saveday'){
    commit(d);                       // آخر خانة قد تحمل تعبيراً لم يُثبَّت بعد
    const list = rows(app);
    // نُثبّت الوقت في مساء اليوم المختار حتى تقع الحركات في اليوم الصحيح
    const base = startOfDay(Date.now()) - d.dayOffset * DAY_MS + 20 * 3600000;
    const fallback = (activeDests()[0] || {}).id;
    const made = [];
    list.forEach((id, i) => {
      const v = +(d.values[id] || 0);
      if (!v) return;
      const e = addEntry({
        amount: v,
        destId: autoDestFor(id) || fallback,
        typeId: id,
        currency: baseCur(),
        occurredAt: new Date(base + i * 1000)
      });
      made.push(e.id);
    });
    if (!made.length) return;
    const label = dayLabel(base);
    app.dayDraft = freshDraft();
    app.closeSub();
    app.toast(`حُفظت ${made.length} ${made.length === 1 ? 'حركة' : 'حركات'} · ${label}`,
              'تراجع', () => app.undoEntries(made));
    app.queueSync();
    return;
  }
  return false;
}
